import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Trash2, Lock, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sinjar_admin_token";

const TABS = [
  { value: "pending", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const StoryCard = ({ s, onApprove, onReject, onDelete }) => (
  <article
    className="border border-white/10 p-6 lg:p-8 bg-[#0A0A0C]"
    data-testid={`admin-story-${s.id}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="text-[#F2EBE5] text-base">{s.name}</div>
        <div className="overline mt-1">
          {s.relationship || "—"}
          {s.location ? ` · ${s.location}` : ""}
        </div>
        {s.contact_email && (
          <a
            href={`mailto:${s.contact_email}`}
            className="text-xs text-[#D4AF37] hover:text-[#E5C07B] mt-2 inline-block"
            data-testid={`admin-story-email-${s.id}`}
          >
            {s.contact_email}
          </a>
        )}
      </div>
      <div className="overline">
        {new Date(s.submitted_at).toLocaleString()}
      </div>
    </div>
    <p className="mt-6 text-[#F2EBE5] font-serif-display leading-relaxed whitespace-pre-wrap">
      {s.story}
    </p>
    {s.moderator_note && (
      <p className="text-xs text-[#A19D98] italic mt-4 border-l border-[#D4AF37]/40 pl-3">
        Note: {s.moderator_note}
      </p>
    )}
    <div className="mt-6 flex flex-wrap gap-3">
      {s.status === "pending" && (
        <>
          <button
            onClick={() => onApprove(s)}
            data-testid={`admin-approve-${s.id}`}
            className="px-4 py-2 bg-[#D4AF37] text-black text-xs tracking-wide hover:bg-[#E5C07B] transition-colors inline-flex items-center gap-2"
          >
            <Check className="w-3 h-3" strokeWidth={1.5} /> Approve & publish
          </button>
          <button
            onClick={() => onReject(s)}
            data-testid={`admin-reject-${s.id}`}
            className="px-4 py-2 border border-white/20 text-[#F2EBE5] text-xs hover:border-[#5E3A3A] hover:text-[#A19D98] transition-colors inline-flex items-center gap-2"
          >
            <X className="w-3 h-3" strokeWidth={1.5} /> Reject
          </button>
        </>
      )}
      <button
        onClick={() => onDelete(s)}
        data-testid={`admin-delete-${s.id}`}
        className="px-4 py-2 border border-white/10 text-[#6E6B67] text-xs hover:border-[#5E3A3A] hover:text-[#A19D98] transition-colors inline-flex items-center gap-2 ml-auto"
      >
        <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
      </button>
    </div>
  </article>
);

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [tab, setTab] = useState("pending");
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (selectedTab = tab, t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stories?status=${selectedTab}`, {
        headers: { "X-Admin-Token": t },
      });
      setStories(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Invalid admin token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load stories.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(tab, token);
  }, [tab, token]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStories([]);
  };

  const review = async (story, action) => {
    let note = null;
    if (action === "reject") {
      note = window.prompt("Optional note for your records:") || null;
    }
    try {
      await axios.post(
        `${API}/admin/stories/${story.id}/review`,
        { action, moderator_note: note },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(action === "approve" ? "Approved and published." : "Rejected.");
      load();
    } catch {
      toast.error("Action failed.");
    }
  };

  const remove = async (story) => {
    if (!window.confirm(`Permanently delete the story by ${story.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/stories/${story.id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-32" data-testid="admin-unlock">
        <Lock className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.25} />
        <div className="overline">Restricted</div>
        <h1 className="font-serif-display text-3xl text-[#F2EBE5] mt-3">
          Moderation panel
        </h1>
        <p className="text-sm text-[#A19D98] mt-4 leading-relaxed">
          Only Ghazy Halou can review submitted stories. Enter the moderation
          token to continue.
        </p>
        <form onSubmit={handleUnlock} className="mt-10 space-y-4">
          <input
            type="password"
            data-testid="admin-token-input"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Moderation token"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
          <button
            type="submit"
            data-testid="admin-unlock-button"
            className="w-full px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20" data-testid="admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="overline text-[#D4AF37]">Moderation panel</div>
          <h1 className="font-serif-display text-3xl lg:text-4xl text-[#F2EBE5] mt-2">
            Submitted stories
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => load()}
            data-testid="admin-refresh"
            className="px-4 py-2 border border-white/15 text-[#F2EBE5] text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
          >
            <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="px-4 py-2 border border-white/15 text-[#A19D98] text-xs hover:border-[#5E3A3A] transition-colors"
          >
            Lock
          </button>
        </div>
      </div>

      <div className="mt-10 border-b border-white/10 flex gap-1" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`admin-tab-${t.value}`}
            className={`px-5 py-3 text-sm tracking-wide border-b-2 transition-colors duration-500 ${
              tab === t.value
                ? "border-[#D4AF37] text-[#D4AF37]"
                : "border-transparent text-[#A19D98] hover:text-[#F2EBE5]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-10 space-y-6">
        {loading && <div className="text-sm text-[#6E6B67]">Loading…</div>}
        {!loading && stories.length === 0 && (
          <div className="text-sm text-[#6E6B67] italic">No stories in this list.</div>
        )}
        {stories.map((s) => (
          <StoryCard
            key={s.id}
            s={s}
            onApprove={(x) => review(x, "approve")}
            onReject={(x) => review(x, "reject")}
            onDelete={remove}
          />
        ))}
      </div>
    </div>
  );
};

export default Admin;
